import React, { useState, useRef, useEffect, useContext } from "react";
import { FaUserCircle, FaSignOutAlt } from "react-icons/fa";
import {
  TOKEN_NAME,
  COMPANYNAME,
  ROLE,
  ROLES,
} from "../../config";
import Cookies from "universal-cookie";
import { Link } from "react-router-dom";
import WorkspaceContext from "../../providers/WorkspaceProvider";
import CompanySwitcherContent from "../company/CompanySwitcherContent";
import useAppBrandInfo from "../../hooks/useAppBrandInfo";

const ProfileDropDown = ({ handleLogout }) => {
  const {
    companies,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isCompaniesLoading,
    selectedAndDefault,
    selectedCompany,
    defaultCompany,
    isSameSelectedAndDefault,
  } = useContext(WorkspaceContext);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const dropdownRef = useRef();
  const cookies = new Cookies();
  const userRole = cookies.get(ROLE);
  const { data: brandInfo } = useAppBrandInfo();

  const handleToggle = () => {
    if (!hasMounted) setHasMounted(true);
    setIsDropdownOpen((prev) => !prev);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={handleToggle}
        className="flex items-center gap-2.5 px-3 py-1.5 rounded-full hover:bg-gray-100 transition cursor-pointer group border border-transparent hover:border-gray-200 select-none text-left"
        aria-label="User Profile and Companies"
      >
        {selectedCompany?.company_name && (
          <span className="text-gray-800 text-sm font-semibold max-w-[100px] xs:max-w-[150px] sm:max-w-[260px] truncate">
            {selectedCompany.company_name}
          </span>
        )}
        <div className="text-2xl text-gray-600 group-hover:text-gray-800 transition shrink-0 flex items-center">
          <FaUserCircle />
        </div>
      </button>

      {hasMounted && (
        <div
          className={`fixed inset-x-2.5 top-16 mx-auto sm:mx-0 sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 w-[calc(100vw-1.25rem)] sm:w-[410px] max-w-[410px] max-h-[calc(100vh-5rem)] flex flex-col bg-white border border-gray-200/90 rounded-3xl shadow-2xl z-50 overflow-hidden origin-top-right transition-all duration-150 ${
            isDropdownOpen
              ? "opacity-100 scale-100 translate-y-0 pointer-events-auto visible ease-out"
              : "opacity-0 scale-[0.98] -translate-y-1 pointer-events-none invisible ease-in"
          }`}
        >
          <CompanySwitcherContent
            companies={companies}
            selectedAndDefault={selectedAndDefault}
            selectedCompany={selectedCompany}
            defaultCompany={defaultCompany}
            isSameSelectedAndDefault={isSameSelectedAndDefault}
            isLoading={isCompaniesLoading}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
            onClose={() => setIsDropdownOpen(false)}
          />

          {/* User Account Navigation Links (Google-style rounded pills) */}
          <div className="px-3.5 pb-3.5 pt-1 flex flex-col gap-1.5 text-xs text-gray-700 shrink-0">
            {userRole === ROLES.EMPLOYEE && (
              <Link
                to="/my-account"
                onClick={() => setIsDropdownOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-gray-50 hover:bg-gray-100 text-xs font-medium text-gray-800 transition border border-gray-100 cursor-pointer"
              >
                <FaUserCircle className="text-gray-500 text-sm shrink-0" />
                <span>My Account</span>
              </Link>
            )}

            <button
              type="button"
              onClick={() => {
                setIsDropdownOpen(false);
                if (handleLogout) handleLogout();
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-gray-50 hover:bg-gray-100 text-xs font-medium text-gray-800 transition border border-gray-100 cursor-pointer text-left"
            >
              <FaSignOutAlt className="text-gray-500 text-sm shrink-0" />
              <span>Sign out</span>
            </button>
          </div>

          {/* App Name and Product Version Footer */}
          <div className="px-4 py-2.5 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-400 select-none bg-gray-50/50">
            <span>
              {brandInfo?.app_name || "iDoM"} {brandInfo?.product_name || "HRMS"}
            </span>
            <span className="font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-[10px]">
              {brandInfo?.product_version || "v1.0.0"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropDown;
